import { Button, Flex, Text } from '@chakra-ui/react'

import { FormSelect } from '../../components/Select'
import { Icon } from '../Icon'

/** Tamanho de página de todas as listagens do painel. */
export const DEFAULT_PAGE_SIZE = 25

/** Opções do seletor de itens por página. */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export interface IPaginatorProps {
  page: number
  totalPages: number
  changePage: (page: number) => void
  /**
   * Liga o seletor de itens por página — os dois vêm juntos, sem `onPageSizeChange` ele não aparece.
   * Voltar para a página 1 é responsabilidade de quem trata a mudança: o Paginator não chama
   * `changePage` sozinho porque várias listagens buscam os dados dentro dele e leriam o tamanho antigo.
   */
  pageSize?: number
  onPageSizeChange?: (pageSize: number) => void
}

/**
 * Seletor à esquerda e setas centralizadas: o canto inferior direito é do `ActionFab`, que é
 * fixo (70px) e cobriria o seletor sempre que a listagem fosse longa o bastante para a
 * paginação chegar perto do rodapé da tela.
 */
export default function Paginator({ page, totalPages, changePage, pageSize, onPageSizeChange }: IPaginatorProps) {
  const showPageSize = pageSize !== undefined && onPageSizeChange !== undefined
  // Uma listagem com tamanho fora da lista (um relatório com 12, por exemplo) ainda precisa
  // enxergar o próprio valor no select em vez de abrir em branco.
  const options =
    pageSize === undefined || PAGE_SIZE_OPTIONS.includes(pageSize)
      ? PAGE_SIZE_OPTIONS
      : [...PAGE_SIZE_OPTIONS, pageSize].sort((a, b) => a - b)

  return (
    <Flex alignItems="center" mt={4}>
      <Flex flex="1" alignItems="center">
        {showPageSize && (
          <>
            <FormSelect
              size="sm"
              w="75px"
              mr="2"
              aria-label="Itens por página"
              value={pageSize}
              onChange={(value) => onPageSizeChange(parseInt(value))}
              options={options.map((option) => ({ value: option, label: String(option) }))}
              hideEmptyOption
            />
            <Text variant="caption" whiteSpace="nowrap">
              por página
            </Text>
          </>
        )}
      </Flex>
      <Flex justifyContent="center" alignItems="center">
        <Button
          colorScheme="magenta"
          size="sm"
          mr={2}
          aria-label="Página anterior"
          isDisabled={page <= 1}
          onClick={() => {
            changePage(page - 1)
          }}
        >
          <Icon name="lni lni-chevron-left" />
        </Button>
        <Text>
          Página {totalPages === 0 ? 0 : page} de {totalPages}
        </Text>
        <Button
          colorScheme="magenta"
          size="sm"
          ml={2}
          mr={2}
          aria-label="Próxima página"
          isDisabled={page >= totalPages}
          onClick={() => {
            changePage(page + 1)
          }}
        >
          <Icon name="lni lni-chevron-right" />
        </Button>
      </Flex>
      <Flex flex="1" />
    </Flex>
  )
}
